import { onScopeDispose, ref, toValue, watch } from 'vue'
import type { MaybeRefOrGetter, Ref } from 'vue'
import { fetchPlatformFeePreview } from '@/api/finance'
import type { PlatformFeePreview } from '@/types/finance'
import { normalisePreviewAmount } from '@/utils/platformFeeAmount'

const DEBOUNCE_MS = 400

export function usePlatformFeePreview(
  organisationId: MaybeRefOrGetter<number>,
  price: MaybeRefOrGetter<string>,
): { preview: Ref<PlatformFeePreview | null>; loading: Ref<boolean>; error: Ref<boolean> } {
  const preview = ref<PlatformFeePreview | null>(null)
  const loading = ref<boolean>(false)
  const error = ref<boolean>(false)

  const cache = new Map<string, PlatformFeePreview>()
  let timer: ReturnType<typeof setTimeout> | null = null
  let latestRequest = 0
  let firstValid = true

  function clearTimer(): void {
    if (timer !== null) {
      clearTimeout(timer)
      timer = null
    }
  }

  async function load(orgId: number, amount: string, key: string): Promise<void> {
    const requestId = ++latestRequest
    loading.value = true
    error.value = false
    try {
      const result = await fetchPlatformFeePreview(orgId, amount)
      if (requestId !== latestRequest) return
      cache.set(key, result)
      preview.value = result
    } catch {
      if (requestId !== latestRequest) return
      preview.value = null
      error.value = true
    } finally {
      if (requestId === latestRequest) loading.value = false
    }
  }

  watch(
    () => [toValue(organisationId), toValue(price)] as const,
    ([orgId, rawPrice]) => {
      clearTimer()
      const amount = normalisePreviewAmount(rawPrice)
      if (amount === null) {
        latestRequest++
        preview.value = null
        error.value = false
        loading.value = false
        return
      }

      const key = `${orgId}:${amount}`
      const cached = cache.get(key)
      if (cached !== undefined) {
        latestRequest++
        preview.value = cached
        error.value = false
        loading.value = false
        firstValid = false
        return
      }

      if (firstValid) {
        firstValid = false
        void load(orgId, amount, key)
        return
      }

      // Invalidate any in-flight response for the previous value right away.
      latestRequest++
      error.value = false
      loading.value = true
      timer = setTimeout(() => {
        timer = null
        void load(orgId, amount, key)
      }, DEBOUNCE_MS)
    },
    { immediate: true },
  )

  onScopeDispose(clearTimer)

  return { preview, loading, error }
}
